import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "../../components/datePicker/datePicker.jsx";
import { apiFetchAccountsPayableByPeriod } from "../../api/backend/admin.js";
import {
  GlassBtn,
  SmallPlainInsetBtn,
  FieldButton,
  ImgTopBtn,
} from "../../components/buttons/index.jsx";
import { EyeIcon, EyeSlashIcon, FilterIcon } from "../../components/icons/index.jsx";
import GlassPopupModal from "../../components/glassPopupModal/glassPopupModal.jsx";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator.js";
import Loader from "../../components/loader/loader.jsx";
import { guiDateFormat } from "../../utils/formatDate.js";
import "./accountsPayable.scss";

export default function AccountsPayable() {
  const [currentContextType, setCurrentContextType] = useState(
    "adminAccountsPayablePage"
  );
  // username filter
  const [userNameValueFilter, setUserNameValueFilter] = useState("");
  const [newUserNameFieldFocus, setNewUserNameFieldFocus] = useState("");
  const [userName, setUserName] = useState("");
  // ––– date filters ––– Date range state
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const [fromDate, setFromDate] = useState(startOfYear.toISOString());
  const [toDate, setToDate] = useState(new Date().toISOString());
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  // Fetching and setting initial values from local storage for the date filters
  const [startDateValue, setStartDateValue] = useState(""); // Initialize as an empty string
  const [endDateValue, setEndDateValue] = useState(""); // Initialize as an empty string
  const [dateOrderError, setDateOrderError] = useState(false);
  // Pagination state
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const [hasMore, setHasMore] = useState(false);
  // Data state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Modal state
  const [modalItems, setModalItems] = useState(null);
  const [backendErrorMessage, setBackendErrorMessage] = useState("");

  // Fetch page of data
  const loadPage = async (reset = false) => {
    setLoading(true);
    setBackendErrorMessage("");
    try {
      const pageOffset = reset ? 0 : offset;
      const { data } = await apiFetchAccountsPayableByPeriod(
        fromDate,
        toDate,
        pageOffset,
        limit,
        userNameValueFilter //
      );
      const txs = reset
        ? data.transactions
        : [...transactions, ...data.transactions];
      setTransactions(txs);
      setOffset(pageOffset + data.transactions.length);
      setHasMore(data.transactions.length === limit);
    } catch (err) {
      console.error("API error fetching AP:", err);
      setBackendErrorMessage(
        "Oops—couldn’t load accounts payable. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Initial load or refetch on date change
  useEffect(() => {
    loadPage(true);
  }, [fromDate, toDate]);

  // Aggregate per user
  const summary = useMemo(() => {
    return Object.values(
      transactions.reduce((acc, tx) => {
        const user = tx.userName || "Unknown";
        if (!acc[user])
          acc[user] = { userName: user, totalAmount: 0, count: 0, items: [] };
        acc[user].totalAmount += parseFloat(tx.totalAmount);
        acc[user].count += 1;
        acc[user].items.push(tx);
        return acc;
      }, {})
    );
  }, [transactions]);

  const handleStartDateChange = (date) => {
    if (!date) return clearStartDate();
    const iso = date.toISOString();
    setStartDateValue(date);
    setFromDate(iso);
    localStorage.setItem(
      getStorageKey("startDateValue", currentContextType),
      iso
    );
  };
  const handleEndDateChange = (date) => {
    if (!date) return clearEndDate();
    const iso = date.toISOString();
    setEndDateValue(date);
    setToDate(iso);
    localStorage.setItem(
      getStorageKey("endDateValue", currentContextType),
      iso
    );
  };
  const clearStartDate = () => {
    const startDateKey = getStorageKey("startDateValue", "eventsList");
    localStorage.removeItem(startDateKey);
    setStartDateValue("");
  };
  const clearEndDate = () => {
    const endDateKey = getStorageKey("endDateValue", "eventsList");
    localStorage.removeItem(endDateKey);
    setEndDateValue("");
  };
  const handleDateErrorChange = (hasError) => {
    console.log("Date order error status:", hasError); // This will log the error status
    setDateOrderError(hasError);
  };
  const applyUsernameFilter = () => {
    setBackendErrorMessage("");
    setOffset(0);
    setUserNameValueFilter(userName);
    loadPage(true);
  };

  useEffect(() => {
    loadPage(true);
  }, [fromDate, toDate, userNameValueFilter]);

  return (
    <div className="accounts-payable" id="accountsPayableTop">
      <h1 className="ap-title">Accounts Payable</h1>
      {/* Date filters */}
      <DatePicker
        onClick={() => setDatePickerOpen(true)}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        clearStartDate={clearStartDate}
        clearEndDate={clearEndDate}
        setStartDateValue={setStartDateValue}
        setEndDateValue={setEndDateValue}
        startDateValue={startDateValue}
        endDateValue={endDateValue}
        onDateErrorChange={handleDateErrorChange}
        showAdditionalDate={false}
        isClearable={true}
        showYearDropdown={true} // show past years
        showMonthDropdown={true}
        numYears={10} // Show the last 10 years
        dateFormat="MMMM d, yyyy Date"
      />
      <div
        className="input-wrapper blinker-wrapper"
        style={{ margin: "16px 0", position: "relative", width: "300px" }}
      >
        <input
          type="text"
          className="input-field"
          placeholder="Username"
          value={userName}
          onChange={({ target: { value } }) => setUserName(value)}
          onFocus={() => setNewUserNameFieldFocus(true)}
          onBlur={() => setNewUserNameFieldFocus(false)}
        />
        {!newUserNameFieldFocus && !userName && (
          <span className="blinking-cursor-gold-color username-role-cursor">
            |
          </span>
        )}

        <FieldButton
          onClick={() => {
            // clear both the box and the applied filter
            setUserName("");
            setUserNameValueFilter("");
            setOffset(0);
          }}
        >
          Clear
        </FieldButton>
      </div>
      {backendErrorMessage && (
        <div
          className="error-message"
          style={{ color: "var(--color-firebrick)", marginTop: 8 }}
        >
          {backendErrorMessage}
        </div>
      )}
         <ImgTopBtn
        image={
          <FilterIcon
            width="25"
            height="25"
            fillColor="var(--color-bloodred)"
            style={{
              marginRight: "5px",
            }}
          />
        }
        imgTopBtnWidth="300px"
        imgTopBtnTextFontSize="1.3rem"
        imgTopBtnFontColor="var(--color-bloodred)"
        imgTopBtnText="Apply Username Filter"
        onClick={applyUsernameFilter}
      />
      {fromDate && toDate && (
        <div className="date-range-display">
          Showing from <strong>{guiDateFormat(fromDate)}</strong> to{" "}
          <strong>{guiDateFormat(toDate)}</strong>
        </div>
      )}
      {/* Loading indicator */}
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      {/* Summary table */}
      {!loading && (
        <table className="summary-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Events Paid</th>
              <th>Total Amount</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((row) => {
              const isOpen =
                modalItems && modalItems[0]?.userName === row.userName;
              return (
                <tr key={row.userName}>
                  <td>{row.userName}</td>
                  <td>{row.count}</td>
                  <td>${row.totalAmount.toFixed(2)}</td>
                  <td>
                    <GlassBtn
                      glassBtnImage={
                        isOpen ? (
                          <EyeSlashIcon
                            height="25px"
                            width="25px"
                            fillColor="var(--color-gold)"
                          />
                        ) : (
                          <EyeIcon
                            height="25px"
                            width="25px"
                            fillColor="var(--color-gold)"
                          />
                        )
                      }
                      glassBtnText={isOpen ? "Hide Details" : "View Details"}
                      glassBtnTextFontSize="1.3rem"
                      glassBtnFontWeight="900"
                      glassBtnFontColor="var(--color-gold)"
                      glassBtnTextDecorationColor="var(--color-gold)"
                      glassHoverStyle={{ height: "60px" }}
                      onClick={() => setModalItems(isOpen ? null : row.items)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {/* Load more button */}
      {!loading && hasMore && (
        <div className="load-more">
          <Loader />
          <SmallPlainInsetBtn
            image={
              <EyeIcon
                height="20px"
                width="20px"
                style={{ marginRight: "3px" }}
              />
            }
            text="Load More"
            width="188px"
            style={{
              fontSize: "1.25rem",
            }}
            onClick={() => loadPage(false)}
          />
        </div>
      )}
      {/* Details Modal */}
      {modalItems && (
        <GlassPopupModal onClose={() => setModalItems(null)}>
          {modalItems.map((tx) => (
            <div className="details-table" key={tx.transactionId}>
              <div className="label">Date:</div>
              <div className="value">{new Date(tx.date).toLocaleString()}</div>
              <div className="label">Amount:</div>
              <div className="value">
                ${parseFloat(tx.totalAmount).toFixed(2)}
              </div>
              <div className="label">Status:</div>
              <div className="value">{tx.status}</div>
              <div className="label">Description:</div>
              <div className="value">{tx.description}</div>

              <div className="label">Event ID:</div>
              <div className="value id">{tx.eventId}</div>

              <div className="label">Schedule ID:</div>
              <div className="value id">{tx.scheduleId}</div>
            </div>
          ))}
        </GlassPopupModal>
      )}
    </div>
  );
}
